import React, {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { GenericMedia } from '@/declarations/GenericMedia';
import { M24PageFinderSelectedItem } from '@/components/Finder/M24PageFinder/M24PageFinderSelectedItem';
import { SortSelector } from '@/components/SortSelector';
import { SortDirection, SortOption, SortType } from '@/declarations/models/SortOption';
import { Status } from '@/declarations/models/Status';
import { Site } from '@/declarations/models/Site';
import { findDefaultPageId } from '@/utils/functions';
import { Checkbox, FormControlLabel } from '@mui/material';
import { PageTreeFilterValues } from '@/declarations/PageTreeFilterValues';
import { PageFolder } from '@/declarations/models/Folder';
import { findBreadCrumb } from '@/utils/folders';
import M24PageFinderPageTree from './M24PageFinderPageTree/M24PageFinderPageTree';
import { Page } from '../../../declarations/models/Page';
import { RecursiveSelectionState, useRecursiveSelectionState } from '../../../hooks/useRecursiveSelectionState';
import { apiTimestampToDate } from '../../../utils/dates';
import Loader from '../../Loader';
import { useStore } from '../../store/Store';
import Container from '../../Container';
import M24PageFinderPageTreeToolbar from './M24PageFinderPageTreeToolbar/M24PageFinderPageTreeToolbar';
import Layout from '../../Layout';
import FinderLayout from '../FinderLayout';
import { Api } from '../../../services/Api';
import { MainCategory } from '../../../declarations/Category';

type SortProperty = keyof Pick<Page, 'title' | 'status' | 'updated_at'>;

interface M24PageFinderContextValue {
  pages: Array<Page>;
  setPages: Dispatch<SetStateAction<Array<Page>>>;
  selectionState: RecursiveSelectionState<Page>;
  expandedPageIds: Array<string>;
  setExpandedPageIds: Dispatch<SetStateAction<Array<string>>>;
  allExpanded: boolean;
  filterValues: PageTreeFilterValues;
  setFilterValues: Dispatch<SetStateAction<PageTreeFilterValues>>;
  hasFilterApplied: boolean;
  reloadPages?: () => void;
  sortProperty: SortProperty;
  setSortProperty: Dispatch<SetStateAction<SortProperty>>;
  sortDirection: SortDirection;
  setSortDirection: Dispatch<SetStateAction<SortDirection>>;
  sortFn: (page1: Page, page2: Page) => number;
  onSelect: (item: Page) => void;
  availableMainCategories: Array<MainCategory>;
}
const M24PageFinderContent = createContext<M24PageFinderContextValue | null>(null);

export function useM24PageFinderContentView(): M24PageFinderContextValue {
  const value = useContext(M24PageFinderContent);
  if (!value) {
    throw new Error('M24PageFinderContent accessed before init');
  }
  return value;
}

export interface M24PageFinderProps {
  open: boolean;
  onClose: () => void;
  onSelectionConfirmed: (selectedItems: Array<GenericMedia<Page>>) => void;
  multiSelect?: boolean;
  isEvent?: boolean;
  hideSiteSelector?: boolean;
  type?: 'page' | 'template';
  lockedLanguage?: string | null;
}

/**
 * The lists the finder showed last, per site and request, kept for the session so opening the finder again shows them
 * at once while they are refreshed. Only the latest few are kept: the lists of a large site are big.
 */
const MAX_CACHED_FINDER_LISTS = 6;
const finderCache = new Map<string, { pages: Array<Page>; folders: Array<PageFolder> }>();
const rememberFinderLists = (key: string, lists: { pages: Array<Page>; folders: Array<PageFolder> }) => {
  finderCache.delete(key);
  finderCache.set(key, lists);
  if (finderCache.size > MAX_CACHED_FINDER_LISTS) {
    finderCache.delete(finderCache.keys().next().value as string);
  }
};

const defaultFilterValues: PageTreeFilterValues = Object.freeze({
  query: '',
  statuses: [],
  main_category: undefined,
  sub_categories: [],
  includePastEvents: true,
  location_ids: [],
});

/**
 * Page tree similar to ContentView in a finder. Most components are copied from ContentView and customized to suit
 * the finder's use case.
 * @param open
 * @param onClose
 * @param onSelect
 * @param multiSelect
 * @param availableMainCategories
 */
export const M24PageFinder: FC<M24PageFinderProps> = ({
  open,
  onClose,
  onSelectionConfirmed,
  multiSelect,
  isEvent,
  hideSiteSelector,
  type = 'page',
  lockedLanguage = null,
}) => {
  const { t } = useTranslation('common');

  const { state } = useStore();
  const { selectedSiteLanguage } = state;
  const selectedSiteId = state.selectedSite?.id || 0;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pages, setPages] = useState<Array<Page>>([]);
  const [pageFolders, setPageFolders] = useState<Array<PageFolder>>([]);
  const [selectedPageFolder, setSelectedPageFolder] = useState<PageFolder | null>(null);
  const [selectedSharedSite, setSelectedSharedSite] = useState<Site | null>(null);
  const [selectedSharedLanguage, setSelectedSharedLanguage] = useState<string | null>(selectedSiteLanguage);
  const [folderBreadcrumbs, setFolderBreadcrumbs] = useState<Array<PageFolder>>([]);
  useEffect(() => {
    if (selectedPageFolder) {
      const breadCrumbs = findBreadCrumb(pageFolders, selectedPageFolder);
      setFolderBreadcrumbs(breadCrumbs);
    } else {
      setFolderBreadcrumbs([]);
    }
  }, [selectedPageFolder, pageFolders]);

  const [expandedPageIds, setExpandedPageIds] = useState<Array<string>>([]);
  // The picked pages are kept here, not in the selection state, which only knows the pages listed now: pages picked
  // on one site or language stay picked when the finder shows another
  const [pickedPages, setPickedPages] = useState<Array<Page>>([]);
  const [filterValues, setFilterValues] = useState<PageTreeFilterValues>({
    ...defaultFilterValues,
    main_category: isEvent ? MainCategory.EVENT : undefined,
  });

  const [sortProperty, setSortProperty] = useState<SortProperty>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);

  const _onClose = () => {
    onClose();
    setPickedPages([]);
    setExpandedPageIds([]);
    setFilterValues({
      ...defaultFilterValues,
      main_category: isEvent ? MainCategory.EVENT : undefined,
    });
  };

  const sortFn = useCallback(
    (page1: Page, page2: Page): number => {
      let a: string | number;
      let b: string | number;

      switch (sortProperty) {
        case 'status':
          a = (page1.status ? t(`Status.${page1.status}`) : '').toLowerCase();
          b = (page2.status ? t(`Status.${page2.status}`) : '').toLowerCase();
          break;
        case 'updated_at':
          a = apiTimestampToDate(page1.updated_at || '')?.getTime() || 0;
          b = apiTimestampToDate(page2.updated_at || '')?.getTime() || 0;
          break;
        case 'title':
        default:
          a = String(page1.title || '').toLowerCase();
          b = String(page2.title || '').toLowerCase();
          break;
      }

      let order = 0;
      if (a < b) {
        order = -1;
      } else if (a > b) {
        order = 1;
      }
      return sortDirection === SortDirection.ASC ? order : -order;
    },
    [sortProperty, sortDirection, t],
  );
  const pagesForSelectedLanguage = useMemo<Array<Page>>(() => {
    return pages.filter((page) => page.locale === (lockedLanguage ?? selectedSharedLanguage ?? selectedSiteLanguage));
  }, [pages, selectedSharedLanguage, selectedSiteLanguage, lockedLanguage]);

  const selectionState = useRecursiveSelectionState<Page>(
    pagesForSelectedLanguage,
    (page) => page.id || 0,
    (page) => page.parent_id || null,
    true,
  );

  const isPicked = useCallback((key: string | number) => pickedPages.some((p) => p.id === Number(key)), [pickedPages]);
  const finderSelectionState = useMemo<RecursiveSelectionState<Page>>(
    () => ({ ...selectionState, isSelected: isPicked }),
    [selectionState, isPicked],
  );

  const selectedItems = useMemo<Array<GenericMedia<Page>>>(() => {
    return pickedPages.map((item) => ({
      id: `${item.id || 0}`,
      title: item.title || '',
      url: item.image_src || '',
      mimetype: item.image?.mimetype,
      description: item.description || '',
      source: item,
    }));
  }, [pickedPages]);

  const hasFilterApplied = useMemo<boolean>(
    () =>
      !!filterValues.query ||
      !!filterValues.statuses.length ||
      typeof filterValues.main_category === 'string' ||
      !!filterValues.sub_categories.length ||
      !!filterValues.location_ids.length,
    [filterValues],
  );

  const contextValue = useMemo<M24PageFinderContextValue>(() => {
    // const handleSelect = (item: Page) => {
    //   onSelect(item);
    //   if (!multiSelect) onClose();
    // };

    const handleSelect = (item: Page) => {
      setPickedPages((prev) => {
        if (prev.some((p) => p.id === item.id)) return prev.filter((p) => p.id !== item.id);
        return multiSelect ? [...prev, item] : [item];
      });
    };

    return {
      pages: pagesForSelectedLanguage,
      selectedPageIds: pickedPages.map((item) => item.id || 0),
      setPages,
      selectionState: finderSelectionState,
      filterValues,
      setFilterValues,
      expandedPageIds,
      setExpandedPageIds,
      allExpanded: expandedPageIds.length === selectionState.itemCount,
      hasFilterApplied,
      sortProperty,
      setSortProperty,
      sortDirection,
      setSortDirection,
      sortFn,
      onSelect: handleSelect,
      availableMainCategories: isEvent
        ? [MainCategory.EVENT]
        : [MainCategory.INFORMATION, MainCategory.EXHIBITION, MainCategory.NONE, MainCategory.KNOWLEDGE],
    };
  }, [
    pagesForSelectedLanguage,
    pickedPages,
    selectionState.itemCount,
    finderSelectionState,
    filterValues,
    expandedPageIds,
    hasFilterApplied,
    sortProperty,
    sortDirection,
    sortFn,
    isEvent,
    multiSelect,
  ]);

  const loadPages = useCallback((): [AbortController['abort'], Promise<Page[]>] => {
    const ctx = Api.getPagesListForSite(selectedSiteId, {
      is_event: isEvent,
      past_events: filterValues.includePastEvents,
      location_ids: filterValues.location_ids,
      site_ids: selectedSharedSite?.id?.toString(),
    });
    const promise = new Promise<Page[]>((res, rej) => {
      ctx.fetchDirect(null).then((fetchedPages) => {
        if (fetchedPages) {
          res(fetchedPages);
        } else {
          rej(new Error('Error fetching pages'));
        }
      });
    });

    return [ctx.abort, promise];
  }, [selectedSharedSite?.id, selectedSiteId, isEvent, filterValues.includePastEvents, filterValues.location_ids]);

  const loadTemplates = useCallback((): [AbortController['abort'], Promise<Page[]>] => {
    const ctx = Api.listPageTemplates(selectedSharedSite?.id ?? selectedSiteId);
    const promise = new Promise<Page[]>((res, rej) => {
      ctx.fetchDirect(null).then((fetchedTemplates) => {
        if (fetchedTemplates?.page_templates) {
          res(fetchedTemplates.page_templates);
        } else {
          rej(new Error('Error fetching templates'));
        }
      });
    });

    return [ctx.abort, promise];
  }, [selectedSharedSite?.id, selectedSiteId]);

  const loadPageFolders = useCallback((): [AbortController['abort'], Promise<PageFolder[]>] => {
    const ctx = Api.getRootFolders(selectedSharedSite?.id ?? selectedSiteId);
    const promise = new Promise<PageFolder[]>((res, rej) => {
      ctx.fetchDirect(null).then((fetchedPages) => {
        if (fetchedPages) {
          res(fetchedPages.folders);
        } else {
          rej(new Error('Error fetching page folders'));
        }
      });
    });

    return [ctx.abort, promise];
  }, [selectedSharedSite?.id, selectedSiteId]);

  // Fetched when the finder opens or what the server is asked for changes (the loaders' dependencies), not on every
  // filter change: the text query and the other filters work on the pages already here. A list seen before is shown
  // at once and refreshed behind it.
  const cacheKey = JSON.stringify([
    type,
    selectedSiteId,
    selectedSharedSite?.id,
    isEvent,
    filterValues.includePastEvents,
    filterValues.location_ids,
  ]);
  useEffect(() => {
    if (!open) return;
    let unmounted = false;

    const cached = finderCache.get(cacheKey);
    if (cached) {
      setPages(cached.pages);
      setPageFolders(cached.folders);
      setIsLoading(false);
    }

    const [abort, pagePromise] = type === 'template' ? loadTemplates() : loadPages();
    const [abortFolders, foldersPromise] = loadPageFolders();

    Promise.all([pagePromise, foldersPromise])
      .then(([pagesResult, foldersResult]) => {
        rememberFinderLists(cacheKey, { pages: pagesResult, folders: foldersResult });
        setPages(pagesResult);
        if (pagesResult.length > 0 && !cached) {
          setExpandedPageIds([String(pagesResult[0].id)]);
        }
        setPageFolders(foldersResult);
        if (!unmounted) setIsLoading(false);
      })
      .catch(() => {
        if (!unmounted) setIsLoading(false);
      });

    return () => {
      unmounted = true;
      abort();
      abortFolders();
    };
  }, [open, cacheKey, type, loadTemplates, loadPages, loadPageFolders, lockedLanguage]);

  const selectedFrontpage = useMemo<Page | undefined>(() => {
    if (lockedLanguage) {
      const id = findDefaultPageId(state.selectedSite, lockedLanguage);
      return pages.find((p) => p.id === id);
    }
    if (!selectedSharedSite && selectedSharedLanguage) {
      const id = findDefaultPageId(state.selectedSite, selectedSharedLanguage);
      return pages.find((p) => p.id === id);
    }
    if (selectedSharedSite && selectedSharedLanguage) {
      const id = findDefaultPageId(selectedSharedSite, selectedSharedLanguage);
      return pages.find((p) => p.id === id);
    }
    return pages.find((p) => p.id === state.selectedSiteDefaultPageId);
  }, [
    pages,
    selectedSharedLanguage,
    selectedSharedSite,
    state.selectedSiteDefaultPageId,
    state.selectedSite,
    lockedLanguage,
  ]);

  // If switching to a site that doesn't have the selected language, switch to the first language available.
  useEffect(() => {
    if (selectedSharedSite?.site_contents?.map((c) => c.locale).includes(selectedSharedLanguage ?? '')) return;
    setSelectedSharedLanguage(
      selectedSharedSite?.site_contents?.[0].locale ?? selectedSharedSite?.default_language ?? selectedSiteLanguage,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSharedSite]);

  const sortOption: SortOption = useMemo(() => {
    return {
      sortBy: sortProperty as SortType,
      order: sortDirection as SortDirection,
    };
  }, [sortProperty, sortDirection]);
  const setSortOption = (option: SortOption) => {
    setSortProperty(option.sortBy as typeof sortProperty);
    setSortDirection(option.order);
  };

  return (
    <M24PageFinderContent.Provider value={contextValue}>
      <FinderLayout<Page>
        open={open}
        onClose={_onClose}
        selectedItems={selectedItems}
        renderSelectedItem={(item) => (
          <M24PageFinderSelectedItem
            item={item}
            onRemove={() => setPickedPages((prev) => prev.filter((p) => p.id !== item.source.id))}
          />
        )}
        onConfirmSelection={() => {
          onSelectionConfirmed(selectedItems);
          _onClose();
        }}>
        <Layout
          headerContent={
            <M24PageFinderPageTreeToolbar
              isEvent={isEvent}
              site={state.selectedSite}
              selectedSharedSite={selectedSharedSite}
              onSelectSharedSite={setSelectedSharedSite}
              selectedLanguage={lockedLanguage ?? selectedSharedLanguage}
              onSelectLanguage={setSelectedSharedLanguage}
              hideSiteSelector={hideSiteSelector}
              selectorType={type}
              disableSelectors={lockedLanguage !== null}
            />
          }>
          {(isEvent || hasFilterApplied) && (
            <Container spaceBetween fullWidth px={4}>
              <SortSelector
                availableOptions={[SortType.TITLE, SortType.UPDATED_AT, SortType.STATUS]}
                sortOption={sortOption}
                setSortOption={setSortOption}
              />
              <Container right fullWidth>
                {[Status.PUBLISHED, Status.DRAFT, Status.ARCHIVED].map((status) => (
                  <FormControlLabel
                    key={status}
                    control={
                      <Checkbox
                        disableRipple
                        checked={filterValues.statuses.includes(status)}
                        onChange={() => {
                          setFilterValues((prev) => ({
                            ...prev,
                            statuses: prev.statuses.includes(status)
                              ? prev.statuses.filter((s) => s !== status)
                              : [...prev.statuses, status],
                          }));
                        }}
                      />
                    }
                    label={t(`Status.${status}`)}
                    labelPlacement='end'
                  />
                ))}
                {isEvent && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        disableRipple
                        checked={!filterValues.includePastEvents}
                        onChange={() => {
                          setFilterValues((prev) => ({
                            ...prev,
                            includePastEvents: !prev.includePastEvents,
                          }));
                        }}
                      />
                    }
                    label={t(`Status.hide_past_events`)}
                    labelPlacement='end'
                  />
                )}
              </Container>
            </Container>
          )}
          <Container p={1} column top fullHeight fullWidth>
            {isLoading ? (
              <Loader />
            ) : (
              <M24PageFinderPageTree
                isEvent={isEvent}
                frontpage={selectedFrontpage}
                pageFolders={pageFolders}
                selectedPageFolder={selectedPageFolder}
                folderBreadcrumbs={folderBreadcrumbs}
                setSelectedPageFolder={setSelectedPageFolder}
              />
            )}
          </Container>
        </Layout>
      </FinderLayout>
    </M24PageFinderContent.Provider>
  );
};

export default M24PageFinder;
